import { useCallback, useRef, useState } from 'react';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import Paper from '@mui/material/Paper';
import Popover, { PopoverOrigin } from '@mui/material/Popover';
import EditRounded from '@mui/icons-material/EditRounded';
import VisibilityIcon from '@mui/icons-material/Visibility';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';

interface Props {
    anchorOrigin?: PopoverOrigin;
    id: string;
    onDelete: (id: string) => void;
    onEdit: (id: string) => void;
}

const position = {
    vertical: 'bottom' as const,
    horizontal: 'left' as const,
};

export const ProposalsActionsMenu = ({ id, anchorOrigin = position, onDelete, onEdit }: Props) => {
    const [isOpen, setOpen] = useState(false);

    const anchorEl = useRef<HTMLButtonElement | null>(null);

    const handleToggle = useCallback(
        (e: React.MouseEvent<HTMLButtonElement>) => {
            e.stopPropagation();
            setOpen((prevState) => !prevState);
        },
        [setOpen],
    );

    const handleClose = useCallback(() => {
        setOpen(false);
    }, [setOpen]);

    const handleClosePopover = useCallback(
        (e: React.MouseEvent<HTMLButtonElement>) => {
            e.stopPropagation();
            handleClose();
        },
        [handleClose],
    );

    const handleEdit = useCallback(() => {
        handleClose();
        onEdit(id);
    }, [id, onEdit, handleClose]);

    const handleRemove = useCallback(() => {
        handleClose();
        onDelete(id);
    }, [id, onDelete, handleClose]);

    return (
        <>
            <Box width="100%" textAlign="center">
                <Tooltip title="Actions">
                    <IconButton ref={anchorEl} onClick={handleToggle}>
                        <MoreVertIcon />
                    </IconButton>
                </Tooltip>
            </Box>
            <Popover
                id={id}
                open={isOpen}
                anchorEl={anchorEl.current}
                onClose={handleClosePopover}
                anchorOrigin={anchorOrigin}
            >
                <Paper>
                    <MenuList>
                        <MenuItem onClick={handleEdit}>
                            <ListItemIcon>
                                <EditRounded />
                            </ListItemIcon>
                            <ListItemText>Edit</ListItemText>
                        </MenuItem>
                        <MenuItem onClick={handleRemove}>
                            <ListItemIcon>
                                <DeleteForeverOutlinedIcon color="error" />
                            </ListItemIcon>
                            <ListItemText color="error">Remove</ListItemText>
                        </MenuItem>
                    </MenuList>
                </Paper>
            </Popover>
        </>
    );
};
